
import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { ArrowLeft, Filter, Bitcoin, ChevronRight, Info, Calendar } from 'lucide-react';

const History: React.FC = () => {
  const { setCurrentScreen, transactions } = useApp();
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');

  const filteredTransactions = transactions.filter(tx => {
    if (filter === 'all') return true;
    if (filter === 'pending') return tx.status === 'pending' || tx.status === 'confirmed';
    if (filter === 'completed') return tx.status === 'completed';
    return true;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex flex-col h-full pb-20">
      <div className="flex items-center mb-6 pt-4">
        <button 
          onClick={() => setCurrentScreen('dashboard')}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold ml-4">Transaction History</h1>
      </div>

      <div className="px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex space-x-2">
            <button 
              onClick={() => setFilter('all')} 
              className={`px-4 py-2 rounded-lg text-sm ${filter === 'all' 
                ? 'bg-neobank-primary text-white' 
                : 'bg-gray-100 text-gray-700'}`}
            >
              All
            </button>
            <button 
              onClick={() => setFilter('pending')} 
              className={`px-4 py-2 rounded-lg text-sm ${filter === 'pending' 
                ? 'bg-neobank-primary text-white' 
                : 'bg-gray-100 text-gray-700'}`}
            >
              Pending
            </button>
            <button 
              onClick={() => setFilter('completed')} 
              className={`px-4 py-2 rounded-lg text-sm ${filter === 'completed' 
                ? 'bg-neobank-primary text-white' 
                : 'bg-gray-100 text-gray-700'}`}
            >
              Completed
            </button>
          </div>
          <button className="p-2 rounded-full hover:bg-gray-100">
            <Filter className="w-5 h-5" />
          </button>
        </div>

        {filteredTransactions.length > 0 ? (
          <div className="mobile-card">
            {filteredTransactions.map((transaction) => (
              <div key={transaction.id} className="py-4 border-b border-gray-100 last:border-0">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center">
                    <div className="bg-gray-100 p-2 rounded-full mr-3">
                      <Bitcoin className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <p className="font-medium">{transaction.cryptoAmount} {transaction.cryptoCurrency}</p>
                      <p className="text-sm text-gray-500">${transaction.amount} USD</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(transaction.status)}`}>
                      {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatDate(transaction.date)}
                  </div>
                  <button 
                    onClick={() => {
                      if (transaction.status === 'confirmed') {
                        setCurrentScreen('atm-qr');
                      } else if (transaction.status === 'pending') {
                        setCurrentScreen('transaction-confirmation');
                      }
                    }}
                    className={`flex items-center text-neobank-primary ${
                      transaction.status !== 'pending' && transaction.status !== 'confirmed' 
                        ? 'opacity-0 pointer-events-none' 
                        : ''
                    }`}
                  >
                    Details <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mobile-card flex flex-col items-center justify-center py-10">
            <div className="bg-gray-100 p-4 rounded-full mb-4">
              <Info className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium mb-2">No transactions found</h3>
            <p className="text-gray-500 text-center mb-6">
              {filter === 'all' 
                ? "You haven't made any transactions yet." 
                : `You don't have any ${filter} transactions.`}
            </p>
            <button 
              onClick={() => setCurrentScreen('transaction')} 
              className="btn-primary"
            >
              Start a Transaction
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
